import React from 'react';
import { useTranslation } from 'react-i18next';
import { CaseUpdatePayload } from '../../../interfaces/realtime-api';
import { useActions, useOvermindState } from '../../state';
import { getCaseManagementLink } from '../../utils';
import { Link } from '../Link/Link';
import { Transition } from '../Transition/Transition';
import { WidgetSubHeading } from '../WidgetSubHeading/WidgetSubHeading';
import css from './MyCasesWidget.module.scss';
import { EmptyWidget } from '../EmptyWidget/EmptyWidget';
import { Spinner } from '../Spinner/Spinner';
import { LoadingState } from '../../../interfaces/internal';

enum SalesforceRecordTypeNames {
    InsightSupport = 'Insight Support',
    StoreInquiry = 'STORE Inquiry',
    MyKalmarSupport = 'MyKalmar General Support',
};
enum MyKalmarCaseTypes {
    Store = 'MyKalmar STORE',
    Insight = 'MyKalmar INSIGHT',
    MyKalmar = 'MyKalmar',
    Automation = 'Automation',
    Other = 'Other',
}

const CaseUpdate: React.FC<CaseUpdatePayload> = (props: CaseUpdatePayload) => {
    const { t } = useTranslation('MyCasesWidget');
    const { casesById } = useOvermindState();
    const { field, caseId } = props;
    const updatedCase = casesById[caseId];

    const fieldName = typeof field === 'string' ? t(field) : t('An unknown value');
    const oldValue = props.oldValue ?? t('n/a');
    const newValue = props.newValue ?? t('n/a');
    const changedAt = props.changedAt ? new Date(props.changedAt).toLocaleString() : null;
    
    const getCaseType = () => {
        switch (updatedCase.RecordTypeName) {
            case SalesforceRecordTypeNames.StoreInquiry:
                return MyKalmarCaseTypes.Store;
            case SalesforceRecordTypeNames.InsightSupport:
                return MyKalmarCaseTypes.Insight;
            case SalesforceRecordTypeNames.MyKalmarSupport:
                return MyKalmarCaseTypes.MyKalmar;
            default:
                return MyKalmarCaseTypes.Automation;
        }
    };

    const renderUpdate = () => {
        const arrowSymbol = '→';
        if (field === 'Comment') {
            return (
                <>
                    {t('New comment')}: <strong>{newValue}</strong>
                </>
            );
        }
        if (props.oldValue && props.newValue) {
            return (
                <>
                    {fieldName} {t('updated')}: <strong>{oldValue}</strong> {arrowSymbol} <strong>{newValue}</strong>
                </>
            );
        }
        if (!props.oldValue && props.newValue) {
            return (
                <>
                    {fieldName} {t('updated')}: <strong>{newValue}</strong>
                </>
            );
        }
        return (
            <>
                {fieldName} {t('updated')}.
            </>
        );
    };

    return (
        <Transition animation={css.slideCaseIn} htmlTagName="li" className={css.caseUpdate}>
            {updatedCase && (
                <Link className={css.link} to={getCaseManagementLink(`/s/case/${caseId}`)}>
                    <div className={css.caseHeader}>
                        <div className={css.caseNumber}>
                            {t('Case')} {updatedCase.CaseNumber}
                        </div>
                        {updatedCase.RecordTypeName ? <div className={css.caseType}>{getCaseType()}</div> : null}
                    </div>
                    <div className={css.caseSubject}>{updatedCase.Subject}</div>
                    {changedAt && (
                        <div className={css.updateTime}>
                            <time dateTime={changedAt}>
                                {t('Updated at')} {new Date(changedAt).toLocaleString()}
                            </time>
                        </div>
                    )}
                    <div className={css.caseUpdateDescription}>{renderUpdate()}</div>
                </Link>
            )}
        </Transition>
    );
};

export const MyCasesWidget: React.FC = () => {
    const { caseUpdates, cases, loadingStates } = useOvermindState();
    const { t } = useTranslation('MyCasesWidget');
    const actions = useActions();

    return loadingStates.cases !== LoadingState.Loading ? (
        <div className={css.myCasesWidget}>
            {caseUpdates.length === 0 && <EmptyWidget type="cases" />}
            {caseUpdates.length > 0 && cases.length > 0 && (
                <>
                    <div className={css.header}>
                        <WidgetSubHeading title={`${t('Recent updates')}:`} className={css.subHeading} />
                        <button className={css.badge} onClick={actions.clearCaseUpdates}>
                            {caseUpdates.length}
                        </button>
                    </div>
                    <ul className={css.caseUpdateList}>
                        {caseUpdates.map((caseUpdate) => (
                            <CaseUpdate
                                {...caseUpdate}
                                key={`${caseUpdate.caseId}-${caseUpdate.field}-${caseUpdate.changedAt}`}
                            />
                        ))}
                    </ul>
                </>
            )}
        </div>
    ) : (
        <Spinner size={0} />
    );
};
